import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import ProfilePictureUpload from '../ProfilePictureUpload';
import ProfilePictureZoom from '../ProfilePictureZoom';
import VerificationSidebar from './sections/VerificationSidebar';
import CredentialManager from './sections/CredentialManager';
import {
  Headphones, User, MapPin, Phone, ShieldCheck, CheckCircle, Calendar,
  Building2, Briefcase, ChevronRight, FileText, Award, Settings,
  Navigation, Bell, MessageSquare, Eye, Gauge, Truck, Users
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICES = [
  'Load sourcing & rate negotiation',
  'Carrier packet setup & broker verification',
  '24/7 driver support & communication',
  'Route optimization & fuel planning',
  'Invoice submission & follow-up',
  'Factoring assistance coordination',
  'Safety & compliance tracking'
];

const TOOLS = [
  'DAT & Truckstop Load Boards', 'TMS Pro / Ascend', 'QuickBooks & Paychex',
  'GPS Fleet Tracking', 'StraightConnect Carrier Sync', 'ELD Compliance Monitoring'
];

const DispatcherProfileComponent = ({ profile, user, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [badges, setBadges] = useState([]);
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    company_name: profile?.company_name || '',
    phone: profile?.phone || '',
    location: profile?.location || '',
    bio: profile?.bio || '',
    dispatch_type: profile?.dispatch_type || 'independent',
    specialties: profile?.specialties || '',
    experience_years: profile?.experience_years || '',
    certifications: profile?.certifications || ''
  });

  useEffect(() => { fetchBadges(); }, []);

  const fetchBadges = async () => {
    try {
      const response = await api.get('/badges/list');
      setBadges(response.data);
    } catch (error) {
      // Badge fetch handled silently
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put('/profile/me', formData);
      toast.success('Profile updated successfully');
      setEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const dispatchLabels = {
    independent: 'Independent Dispatcher', carrier_attached: 'Carrier-Attached',
    freight_coordinator: 'Freight Coordinator', dispatch_service: 'Dispatch Service Company'
  };

  const certList = (profile?.certifications || formData.certifications || '').split(',').map(c => c.trim()).filter(Boolean);

  return (
    <div className="space-y-6">
      {/* Hero Header */}
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-violet-950 via-purple-900 to-violet-950"></div>
        <div className="absolute inset-0 opacity-[0.06]" style={{backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`}}></div>

        <div className="relative px-8 py-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <ProfilePictureZoom src={user?.profile_picture} alt={formData.company_name || 'Dispatcher'} size="w-24 h-24">
                <ProfilePictureUpload currentPicture={user?.profile_picture} onUploadSuccess={() => { if (onUpdate) onUpdate(); }} />
              </ProfilePictureZoom>
              <div>
                <span className="inline-block px-3 py-1 bg-violet-500/20 backdrop-blur-sm rounded-full text-xs font-semibold text-violet-300 border border-violet-400/30 mb-2 tracking-wider uppercase">
                  Dispatcher
                </span>
                <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="dispatcher-profile-title">
                  {formData.company_name || profile?.name || 'Dispatcher Profile'}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-violet-300/70 text-sm">
                  <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{profile?.location || 'Location not set'}</span>
                  {profile?.experience_years && <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{profile.experience_years} yrs</span>}
                </div>
              </div>
            </div>

            {/* Status Card */}
            <div className="bg-white/5 backdrop-blur-md rounded-xl p-5 border border-white/10 min-w-[180px]" data-testid="dispatcher-status-card">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/20">
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider">Status</p>
                  <p className="text-sm font-bold text-white capitalize">{profile?.verification_status === 'verified' ? 'Verified Partner' : 'Pending'}</p>
                </div>
              </div>
              {certList.length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap gap-1">
                  {certList.slice(0, 3).map(c => (
                    <span key={c} className="px-2 py-0.5 bg-white/10 rounded text-[10px] text-white">{c}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Company Identity */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Building2 className="h-5 w-5 text-violet-500" /> Dispatch Company Info
              </h2>
              <button onClick={() => setEditing(!editing)} className="text-sm text-violet-600 hover:text-violet-700 font-medium" data-testid="edit-dispatcher-profile-btn">
                {editing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>

            {editing ? (
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Contact Name</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" data-testid="dispatcher-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Company Name</label>
                    <input type="text" value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" placeholder="S Connect Sync Route Dispatcher" data-testid="dispatcher-company-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Phone</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" data-testid="dispatcher-phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Location</label>
                    <input type="text" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" data-testid="dispatcher-location-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Dispatch Type</label>
                    <select value={formData.dispatch_type} onChange={(e) => setFormData({...formData, dispatch_type: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" data-testid="dispatcher-type-select">
                      <option value="independent">Independent Dispatcher</option>
                      <option value="carrier_attached">Carrier-Attached</option>
                      <option value="freight_coordinator">Freight Coordinator</option>
                      <option value="dispatch_service">Dispatch Service Company</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1">Years of Experience</label>
                    <input type="number" value={formData.experience_years} onChange={(e) => setFormData({...formData, experience_years: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" data-testid="dispatcher-experience-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Specialties</label>
                  <input type="text" value={formData.specialties} onChange={(e) => setFormData({...formData, specialties: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" placeholder="Box truck, Straight truck, Local, OTR" data-testid="dispatcher-specialties-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Certifications</label>
                  <input type="text" value={formData.certifications} onChange={(e) => setFormData({...formData, certifications: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" placeholder="FMCSA Compliant, WOSB, Minority-Owned" data-testid="dispatcher-certifications-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1">Mission / Description</label>
                  <textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-sm" placeholder="Tell carriers and drivers about your dispatch services..." data-testid="dispatcher-bio-input" />
                </div>
                <button type="submit" disabled={loading} className="bg-violet-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50" data-testid="save-dispatcher-profile-btn">
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            ) : (
              <div className="space-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { icon: User, label: 'Contact', value: profile?.name },
                    { icon: Building2, label: 'Company', value: profile?.company_name },
                    { icon: Phone, label: 'Phone', value: profile?.phone },
                    { icon: MapPin, label: 'Location', value: profile?.location },
                    { icon: Headphones, label: 'Dispatch Type', value: dispatchLabels[profile?.dispatch_type] || profile?.dispatch_type, highlight: true },
                    { icon: Calendar, label: 'Experience', value: profile?.experience_years ? `${profile.experience_years} years` : null },
                  ].map(({ icon: Icon, label, value, highlight }) => (
                    <div key={label} className={`flex items-center gap-3 p-3 rounded-lg ${highlight ? 'bg-violet-50 border border-violet-100' : 'bg-slate-50/80'}`}>
                      <Icon className={`h-4 w-4 flex-shrink-0 ${highlight ? 'text-violet-500' : 'text-slate-400'}`} />
                      <div>
                        <p className={`text-[11px] uppercase tracking-wider font-medium ${highlight ? 'text-violet-500' : 'text-slate-400'}`}>{label}</p>
                        <p className="text-sm font-medium text-slate-800">{value || 'Not set'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {certList.length > 0 && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-2">Certifications</p>
                    <div className="flex flex-wrap gap-1.5">
                      {certList.map(c => (
                        <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 rounded-md text-xs font-medium border border-green-100">
                          <CheckCircle className="h-3 w-3" />{c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {profile?.specialties && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-2 flex items-center gap-1"><Truck className="h-3.5 w-3.5" /> Specialties</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.specialties.split(',').map(s => (
                        <span key={s.trim()} className="px-2.5 py-1 bg-violet-50 text-violet-700 rounded-md text-xs font-medium border border-violet-100">{s.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {profile?.bio && (
                  <div className="pt-4 mt-3 border-t border-slate-100">
                    <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium mb-1">Mission</p>
                    <p className="text-sm text-slate-700 leading-relaxed">{profile.bio}</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>

          {/* Services Offered */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-violet-500" /> Services Offered
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SERVICES.map(s => (
                <div key={s} className="flex items-start gap-2.5 p-3 border border-slate-100 rounded-lg hover:border-violet-200 transition-colors">
                  <CheckCircle className="h-4 w-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{s}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Operations Dashboard */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Gauge className="h-5 w-5 text-violet-500" /> Real-Time Operations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                <Navigation className="h-6 w-6 text-green-600 mb-2" />
                <p className="text-sm font-semibold text-green-900">GPS Tracking</p>
                <p className="text-xs text-green-600 mt-0.5">Live driver locations</p>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                <Bell className="h-6 w-6 text-blue-600 mb-2" />
                <p className="text-sm font-semibold text-blue-900">Status Alerts</p>
                <p className="text-xs text-blue-600 mt-0.5">Route change notifications</p>
              </div>
              <div className="p-4 bg-violet-50 rounded-lg border border-violet-100">
                <MessageSquare className="h-6 w-6 text-violet-600 mb-2" />
                <p className="text-sm font-semibold text-violet-900">Messaging</p>
                <p className="text-xs text-violet-600 mt-0.5">Dispatch-driver chat</p>
              </div>
            </div>
          </motion.div>

          {/* Technology Stack */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-xl p-6"
          >
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5 text-violet-500" /> Technology Stack
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {TOOLS.map(t => (
                <div key={t} className="flex items-center gap-2 p-2.5 bg-slate-50 border border-slate-100 rounded-lg">
                  <div className="w-1.5 h-1.5 bg-violet-500 rounded-full flex-shrink-0"></div>
                  <span className="text-xs text-slate-700">{t}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
            className="bg-violet-950 rounded-xl p-6 text-white"
          >
            <h2 className="text-lg font-bold mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link to="/jobs" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5">
                <Briefcase className="h-5 w-5 text-violet-400" />
                <div><p className="text-sm font-medium">Find Carriers</p><p className="text-[11px] text-violet-300/60">Browse network</p></div>
                <ChevronRight className="h-4 w-4 text-violet-500/40 ml-auto" />
              </Link>
              <Link to="/referrals" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5">
                <Users className="h-5 w-5 text-amber-400" />
                <div><p className="text-sm font-medium">Referrals</p><p className="text-[11px] text-violet-300/60">Grow network</p></div>
                <ChevronRight className="h-4 w-4 text-violet-500/40 ml-auto" />
              </Link>
              <Link to="/dispatcher-profile" className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors border border-white/5">
                <Eye className="h-5 w-5 text-sky-400" />
                <div><p className="text-sm font-medium">Public Profile</p><p className="text-[11px] text-violet-300/60">View as others</p></div>
                <ChevronRight className="h-4 w-4 text-violet-500/40 ml-auto" />
              </Link>
            </div>
          </motion.div>

          {/* Credentials */}
          <CredentialManager badges={badges} onBadgesChange={fetchBadges} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <VerificationSidebar profile={profile} badges={badges} color="violet" />

          {/* Core Values */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-xl p-5"
          >
            <h3 className="text-sm font-bold text-slate-900 mb-3">Core Values</h3>
            <div className="flex flex-wrap gap-1.5">
              {['Transparency', 'Communication', 'Consistency'].map(v => (
                <span key={v} className="px-2.5 py-1 bg-violet-50 text-violet-700 rounded-md text-xs font-medium border border-violet-100">{v}</span>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}
            className="bg-white border border-slate-200 rounded-xl p-5"
          >
            <h3 className="text-sm font-bold text-slate-900 mb-3">Resources</h3>
            <div className="space-y-2">
              <Link to="/resources" className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg hover:bg-violet-50 transition-colors text-sm text-slate-700">
                <FileText className="h-4 w-4 text-violet-500" /> Dispatcher Guides
              </Link>
              <Link to="/help-center" className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg hover:bg-violet-50 transition-colors text-sm text-slate-700">
                <Award className="h-4 w-4 text-violet-500" /> Help Center
              </Link>
            </div>
          </motion.div>

          {/* Upgrade CTA */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-violet-600 to-purple-600 text-white rounded-xl p-5"
          >
            <h3 className="text-sm font-bold mb-2">Your Business Profile</h3>
            <p className="text-xs text-violet-200 mb-3">Click the button to view business profile page.</p>
            <Link to="/profile" className="block w-full bg-white text-violet-600 px-4 py-2 rounded-lg text-xs font-semibold text-center hover:bg-violet-50 transition-colors" data-testid="dispatcher-upgrade-cta">
              View Profile
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default DispatcherProfileComponent;
